import { TrendingUp, Users, Wallet, ShieldCheck } from "lucide-react";

const STATS = [
  { value: "1–4", label: "Investors per property", caption: "A small, vetted partnership" },
  { value: "0", label: "Weeks of personal use", caption: "Pure long‑term investment" },
  { value: "12", label: "Monthly distributions a year", caption: "Net rent paid into your account" },
];

const PILLARS = [
  {
    icon: Users,
    title: "Matched co‑investors",
    body: "We pair 1–4 families with aligned horizons, risk appetites and exit plans — using the same vetting framework as our Co‑Own programme.",
  },
  {
    icon: ShieldCheck,
    title: "Trusted partners, introduced",
    body: "Lawyer, mortgage broker, currency agent and property finder — all from our network, all on your side. You sign nothing without counsel.",
  },
  {
    icon: Wallet,
    title: "Long‑term tenant in place",
    body: "We source and place a quality long‑term tenant. Your management company collects rent, handles repairs, files taxes and remits net income monthly.",
  },
  {
    icon: TrendingUp,
    title: "Transparent on your app",
    body: "Real‑time rent received, expenses, valuations and tenant correspondence — all in the same owner app. No back‑and‑forth, no surprises.",
  },
];

export default function CoInvest() {
  return (
    <section
      id="co-invest"
      data-testid="co-invest-section"
      className="relative bg-[var(--bone-soft)] text-[var(--ink)]"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1400px] py-24 md:py-36 lg:py-44">
        {/* Heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 reveal">
            <div className="text-overline text-[var(--ink)]/55 mb-6 flex items-center gap-4">
              <span className="h-px w-10 bg-[var(--ink)]/30" />
              <span>The other path</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.8rem] leading-[1.05] tracking-tight">
              Don’t want to stay?
              <br />
              <span className="italic">
                Just <span style={{ color: "#E87A4C" }} className="font-semibold not-italic">Co‑Invest.</span>
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-[var(--ink)]/75 text-base md:text-lg leading-relaxed font-light max-w-md">
              Some families want a home in Israel. Others want capital in Israel. <br className="hidden md:inline" />
              Same principles, same care — minus the suitcase. You partner with 1–4 vetted co‑investors,
              place a long‑term tenant, and receive monthly rent while your asset grows.
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-soft)] border border-[var(--border-soft)]">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`coinvest-stat-${i}`}
              className="bg-[var(--bone)] p-10 md:p-14 reveal"
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <div
                className="font-serif-display text-7xl md:text-8xl lg:text-[8.5rem] leading-none tracking-tighter"
                style={{ color: "#E87A4C" }}
              >
                {s.value}
              </div>
              <div className="mt-4 text-overline text-[var(--ink)]/60">{s.label}</div>
              <div className="mt-3 text-[var(--ink)]/70 text-base font-light">{s.caption}</div>
            </div>
          ))}
        </div>

        {/* Pillars */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-soft)] border border-[var(--border-soft)]">
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`coinvest-pillar-${i}`}
              className="bg-[var(--bone)] p-8 md:p-10 flex flex-col gap-5 reveal"
              style={{ transitionDelay: `${(i % 4) * 0.07}s` }}
            >
              <p.icon className="h-6 w-6" style={{ color: "#E87A4C" }} strokeWidth={1.6} />
              <h3 className="font-serif-display text-2xl md:text-[1.6rem] leading-tight tracking-tight">
                {p.title}
              </h3>
              <p className="text-[var(--ink)]/65 text-sm md:text-base leading-relaxed font-light">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
